import React, { useEffect } from "react";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import ListItem from "@mui/material/ListItem";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import InfoIcon from "@mui/icons-material/Info";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
//import FileIcon from "@mui/icons-material/FileOpen";
//import FunctionsIcon from "@mui/icons-material/Functions";
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from "@mui/icons-material/Delete";
import CropRotateIcon from "@mui/icons-material/CropRotate";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import CopyAllIcon from "@mui/icons-material/CopyAll";
import SendIcon from "@mui/icons-material/Send";
import PermIdIcon from "@mui/icons-material/PermIdentitySharp";

import useActions, { useAppDispatch, useAppSelector } from "../../app/hooks";
import IconButton from "@mui/material/IconButton";
import ImageListItem from "@mui/material/ImageListItem";
import ImageListItemBar from "@mui/material/ImageListItemBar";
import Tooltip from "@mui/material/Tooltip";
import { Workspace } from "../../types/Dash";
import { Folder } from "../../types/Folder";
import { Role } from "../../types/Role";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import DashInfoForm from "./DashInfoForm";
import DashCloneForm from "./DashCloneForm";
import DashMoveForm from "./DashMoveForm";
import Badge from "@mui/material/Badge";
import CircularProgress from "@mui/material/CircularProgress";
//import DashCreationForm from "../generation/DashCreationForm";

import AttachRoleForm from "./AttachRoleForm";
import { RollerShades } from "@mui/icons-material";

import AccountTreeIcon from "@mui/icons-material/AccountTree";
import {
  selectFileLineCounts,
  openFuncMap,
  setRoleId,
  setUsersInRole,
} from "./librarySlice";

import {
  setFileDataChanged,
  setViewCodeState,
} from "../editor/dashEditorSlice";

/**
 * A single library item showing a Dash.
 *
 * @export
 * @param {{
 *   dash: Dash;
 * }} props
 * @return {*}
 */
export default function DashLibraryFile(props: {
  workspaceRole: string;
  file: string;
  size: number;
  repoId: number;
  workspaces: Array<Workspace> | null;
  currentWorkspace: Workspace | null;
  folderName: string;
}) {
  const actions = useActions();
  const dispatch = useAppDispatch();

  const location = props.folderName == "/" ? props.file : props.folderName + "/" + props.file;
  const lineCounts = useAppSelector(selectFileLineCounts);
  const lineCount = lineCounts[location];

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const [infoOpen, setInfoOpen] = React.useState(false);

  const handleFolderClicked = () => {
    const location = props.folderName == "/" ? props.file : props.folderName + "/" + props.file;

    dispatch(setFileDataChanged(false));

    dispatch(setViewCodeState(3));

    actions.openFile({ repoId: props.repoId, file_name: location });
  };

  const handleInfoClicked = () => {
    setAnchorEl(null);
    setInfoOpen(true);
  };

  const handleMapClicked = () => {
    setAnchorEl(null);
    dispatch(openFuncMap({ filePath: location, repoId: props.repoId }));
    actions.mapFile({ repoId: props.repoId, filePath: location });
  };

  const handleDeleteClicked = () => {
    setAnchorEl(null);
    const location = props.folderName == "/" ? props.file : props.folderName + "/" + props.file;
    if (window.confirm(`Are you sure you want to delete "${props.file}"?`)) {
      actions.deleteFile({ repoId: props.repoId, file_name: location });
    }
  };

  const handleRenameClicked = () => {
    setAnchorEl(null);
    const location = props.folderName == "/" ? props.file : props.folderName + "/" + props.file;
    const newName = window.prompt(`Enter new name for "${props.file}":`, props.file);
    if (newName !== null && newName.trim() !== "" && newName !== props.file) {
      // Construct new file path with the new name
      const folderPath = props.folderName == "/" ? "" : props.folderName + "/";
      const newLocation = folderPath + newName;
      actions.renameFile({ 
        repoId: props.repoId, 
        file_name: location,
        new_repo_file_name: newLocation
      });
    }
  };
  
  return (
    <ListItem
      sx={{
        bgcolor: "background.paper",
        marginBottom: "10px",
        width: "60vw",
        cursor: "pointer",
        "&:hover": {
          bgcolor: "action.hover",
        },
      }}
      secondaryAction={
        <IconButton aria-label="comment"
          onClick={handleOpenMenu}>
          ...
        </IconButton>
      }
    >
      <ListItemText primary={<div>{props.file} : <strong>{props.size}</strong> bytes{lineCount !== undefined ? <> &middot; <strong>{lineCount}</strong> lines</> : null}</div>} onClick={(e) => handleFolderClicked()}/>

      <Menu
        id="demo-positioned-menu"
        aria-labelledby="demo-positioned-button"
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}

      >
        <MenuItem onClick={handleInfoClicked}>
          <ListItemIcon>
            <InfoIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>File Properties...</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleMapClicked}>
          <ListItemIcon>
            <AccountTreeIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Map functions...</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleRenameClicked}>
          <ListItemIcon>
            <DriveFileRenameOutlineIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Rename File</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDeleteClicked}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete File</ListItemText>
        </MenuItem>
      </Menu>

      <Dialog open={infoOpen} onClose={() => setInfoOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>File properties</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            <InfoRow label="File name" value={props.file} />
            <InfoRow label="Size (bytes)" value={String(props.size)} />
            <InfoRow label="Repo id" value={String(props.repoId)} />
            <InfoRow label="Folder" value={props.folderName} />
            <InfoRow
              label="Full path"
              value={props.folderName === "/" ? props.file : props.folderName + "/" + props.file}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
  </ListItem>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
        {value}
      </Typography>
    </Box>
  );
}
